'use client';

/**
 * The Meridian chat page: sidebar, chat area, and the page-level modals.
 *
 * This file is the LAYOUT. Everything the page knows lives in the controller.
 * The pieces are in `./chat-page/`:
 *  - `useChatPageController.ts` — contexts, queries, seeds, CRUD, effects, tabs
 *  - `tab-navigation.ts`        — pure: visible tabs, neighbour-on-close
 *  - `useChatPageTabs.ts`       — the tab controller (open/close/delete/archive)
 *  - `ChatTabbedArea.tsx`       — tab strip + one mounted pane per open chat
 *  - `ChatSinglePaneArea.tsx`   — loading / active chat / start screen
 *  - `ChatErrorToast.tsx`       — the dismissible page error
 */

import React from 'react';
import { ChatSidebarTabs } from './ChatSidebarTabs';
import { ShareChatModal } from './ShareChatModal';
import { ChatSettingsModal } from './ChatSettingsModal';
import { ChatCapabilitiesModal } from './ChatCapabilitiesModal';
import { ErrorBoundary } from './ErrorBoundary';
import { MobileMenuButton } from './MobileMenuButton';
import { isShareChatEnabled } from '../lib/utils/feature-flags';
import { useChatPageController } from './chat-page/useChatPageController';
import { ChatTabbedArea } from './chat-page/ChatTabbedArea';
import { ChatSinglePaneArea } from './chat-page/ChatSinglePaneArea';
import { ChatErrorToast } from './chat-page/ChatErrorToast';
import { shimmerStyle } from './chat-page/shimmer';
import { CHAT_LAYOUT } from '../styles/chat-layout';
import { SlackAvailabilityCallout } from './SlackAvailabilityCallout';
import { ChatActivityProvider } from '../contexts/ChatActivityContext';
import type { ChatPageProps } from './chat-page/types';

export function ChatPage({
  apiBaseUrl = '/api/meridian',
  locale = 'en',
  botAvatarUrl,
  userInitials,
  userAvatarUrl,
  toolCapabilities = [],
  mcpConnectionsHref,
  launchRequest = null,
  compactStartScreen = false,
  compactSidebar = false,
  sidebarBehavior = 'responsive',
  meridianLogoUrl = '/meridian.png',
  meridianFetchEnabled = true,
  showSlackCallout = true,
  slackCalloutMentionsSearch = false,
}: ChatPageProps) {
  const page = useChatPageController({ apiBaseUrl, meridianFetchEnabled, launchRequest });
  const { selectedChatId, chatsQuery, messagesQuery } = page;

  // Props every chat surface needs, whichever branch renders.
  const surface = {
    apiBaseUrl,
    locale,
    isLoading: page.isLoading,
    initialQuery: page.initialQuery,
    initialFiles: page.initialFiles,
    initialSkill: page.initialSkill,
    userContext: page.userContext,
    onStartChat: page.handleStartChatWithQuery,
    botAvatarUrl,
    userInitials,
    userAvatarUrl,
    meridianLogoUrl,
    mcpConnectionsHref,
    compactStartScreen,
    showSlackCallout,
    slackCalloutMentionsSearch,
    // Hosts without Meridian API access (extension pre-auth) can't load the
    // model catalog — hide the picker rather than show a dead chip.
    showModelPicker: meridianFetchEnabled,
  };

  const pageLayoutClassName =
    sidebarBehavior === 'split-fixed'
      ? 'flex flex-row h-full min-h-0 bg-background'
      : `${CHAT_LAYOUT.page} h-full`;

  return (
    <ErrorBoundary>
      {/* Publishes "which chats are working right now" to the tab strip AND the
          sidebar rows. Mounted regardless of the tab preference: running two
          chats at once and watching both from the sidebar does not require
          tabs. */}
      <ChatActivityProvider
        apiBaseUrl={apiBaseUrl}
        activeChatId={selectedChatId}
        pollEnabled={meridianFetchEnabled}
      >
      <style>{shimmerStyle}</style>
      {sidebarBehavior !== 'split-fixed' && (
        <MobileMenuButton
          isOpen={page.isSidebarOpen}
          onClick={page.handleToggleSidebar}
          alwaysVisible={sidebarBehavior === 'drawer-only'}
        />
      )}
      <div className={pageLayoutClassName}>
        <ChatSidebarTabs
          chats={chatsQuery.data}
          selectedChatId={selectedChatId}
          onSelectChat={page.setUrlChatId}
          onCreateChat={page.handleCreateChat}
          onRestoreChat={page.handleRestoreChat}
          onDeleteChat={page.handleDeleteChat}
          onDeleteAllChats={page.handleDeleteAllChats}
          isLoading={chatsQuery.isLoading}
          locale={locale}
          isOpen={page.isSidebarOpen}
          onClose={page.handleCloseSidebar}
          onLoadMoreChats={() => chatsQuery.fetchNextPage()}
          hasMoreChats={chatsQuery.hasNextPage}
          isLoadingMoreChats={chatsQuery.isFetchingNextPage}
          onOpenSettings={() => page.openModal('chat-settings')}
          onOpenCapabilities={
            toolCapabilities.length > 0 ? () => page.openModal('chat-capabilities') : undefined
          }
          mcpConnectionsHref={mcpConnectionsHref}
          compactMode={compactSidebar}
          sidebarBehavior={sidebarBehavior}
          meridianLogoUrl={meridianLogoUrl}
        />

        {/* Main Chat Area - full width */}
        <div className={CHAT_LAYOUT.chatArea}>
          {/* Slack availability — desktop-only. Per owner request the chat page
              shows no Slack info on mobile; the banner still satisfies
              MERIDIAN_SLACK_AVAILABILITY on desktop. Sits above the chat header
              so it stays visible across start screen, loading, and active chat
              states. Dismissible (localStorage). */}
          {showSlackCallout && (
            <div className="hidden sm:block">
              <SlackAvailabilityCallout
                variant="banner"
                mentionSearch={slackCalloutMentionsSearch}
              />
            </div>
          )}
          {page.tabsEnabled ? (
            <ChatTabbedArea
              {...surface}
              visibleTabs={page.visibleTabs}
              selectedChatId={selectedChatId}
              onSelectChat={page.setUrlChatId}
              onCloseTab={page.handleCloseTab}
              onMessageSent={page.clearInitialQuery}
              onShare={page.handleShareChatFromPane}
              onArchive={page.handleArchiveChatAndCloseTab}
              onDelete={page.handleDeleteChatAndCloseTab}
              onTitleResolved={page.setTabTitle}
              onBackToList={page.handleBackToList}
            />
          ) : (
            <ChatSinglePaneArea
              {...surface}
              selectedChatId={selectedChatId}
              selectedChat={page.selectedChat}
              isMessagesLoading={messagesQuery.isLoading}
              messages={messagesQuery.data}
              hasMoreMessages={messagesQuery.hasNextPage}
              isLoadingOlderMessages={messagesQuery.isFetchingNextPage}
              onMessageSent={page.clearInitialQuery}
              onLoadOlderMessages={() => messagesQuery.fetchNextPage()}
              onShare={page.openShareModal}
              onArchive={page.handleArchiveChat}
              onDelete={page.handleDeleteChat}
              onBackToList={page.handleBackToLoadingList}
            />
          )}
        </div>

        {/* Share Modal */}
        {isShareChatEnabled() && page.showShareModal && selectedChatId && (
          <ShareChatModal
            chatId={selectedChatId}
            apiBaseUrl={apiBaseUrl}
            onClose={page.closeShareModal}
            onSuccess={() => {
              page.closeShareModal();
              messagesQuery.refetch();
            }}
          />
        )}

        {/* Settings Modal – opened from sidebar gear icon via global modal stack */}
        <ChatSettingsModal apiBaseUrl={apiBaseUrl} />

        {/* Capabilities Modal – opened from sidebar lightbulb icon */}
        {toolCapabilities.length > 0 && (
          <ChatCapabilitiesModal
            toolCapabilities={toolCapabilities}
            mcpConnectionsHref={mcpConnectionsHref}
          />
        )}

        {page.error && <ChatErrorToast error={page.error} onDismiss={page.dismissError} />}
      </div>
      </ChatActivityProvider>
    </ErrorBoundary>
  );
}
